import React, { FormEvent, useState, useEffect } from 'react';
import queryString from 'query-string';
import { Link } from '@leafygreen-ui/typography';

// contexts
import { useAppUserParams } from 'js/auth/context/AppUserContext';

// utils
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';

// services
import organizationApi from 'js/common/services/api/organizationApi';

// types
import { VercelProject } from 'js/common/types/vercel';
import { Search } from 'js/auth/types/search';

// components
import VercelPage from 'js/common/components/Vercel/VercelPage';
import VercelCreateAndManageLinking from 'js/common/components/Vercel/VercelCreateAndManageLinking';
import { useRequestParams } from 'js/common/context/RequestParamsContext';

export default function VercelClusterLinkingPage({
  location: { search } = { search: '' },
  windowLocation = window.location,
}: {
  location?: Pick<Location, 'search'>;
  windowLocation?: Pick<Location, 'assign'>;
}) {
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedVercelProjects, setSelectedVercelProjects] = useState<Array<VercelProject>>([]);
  const [hasLinkingError, setHashLinkingError] = useState(false);
  const { orgName, orgId, vercelUserId, next } = queryString.parse(search) as Search;
  const { centralUrl } = useRequestParams();
  const { loadAppUserParams } = useAppUserParams();

  useEffect(() => {
    loadAppUserParams();
  }, [loadAppUserParams]);

  const redirectToVercel = () => {
    const vercelURI = decodeURI(next as string);

    if (new URL(vercelURI).hostname === 'vercel.com') {
      windowLocation.assign(vercelURI);
    }
  };

  const linkExistingCluster = async (e: FormEvent<HTMLFormElement>, groupId: string): Promise<void> => {
    e.preventDefault();

    if (orgId && vercelUserId && next && groupId) {
      try {
        await organizationApi.linkClusterToVercelProjects({
          centralUrl,
          orgId,
          vercelUserId,
          cluster: selectedCluster,
          vercelProjects: selectedVercelProjects,
          groupId,
        });
      } catch (error) {
        sendError({ error, team: CloudTeams.CoreIam });
        setHashLinkingError(true);
      } finally {
        redirectToVercel();
      }
    }
  };

  return (
    <>
      <VercelPage pageTitle="Vercel Integration">
        <VercelCreateAndManageLinking
          orgName={orgName}
          orgId={orgId}
          onChangeCluster={setSelectedCluster}
          onChangeVercelProjects={setSelectedVercelProjects}
          onSubmit={linkExistingCluster}
          isSubmitDisabled={selectedVercelProjects.length === 0 || selectedCluster === ''}
          location={location}
          linkingError={
            hasLinkingError && (
              <>
                The integration is set up successfully. However, an error happened when attempting to link the Atlas
                cluster to Vercel projects. Please visit the{' '}
                <Link href={`${centralUrl}/v2#/org/${orgId}/integrations`}>Organization&apos;s Integrations</Link> page
                to try again. Click <Link onClick={redirectToVercel}>here</Link> to close this window and return to
                Vercel.
              </>
            )
          }
        />
      </VercelPage>
    </>
  );
}
